/** @jsx jsx */
import { jsx } from "@emotion/core"
import { Modal } from "@cd2/components/build/es6"
import { useCallback, useEffect, useRef } from "react"
import { explodePromise } from "../utils/explodePromise"

type ManagedDialogProps<Res, Data = void> = {
  dialogData: Data
  onResolve(response: Res): void
  registerOutsideClick: (func: () => void) => void
}

export function useManagedDialog<Res, Data = void>({
  width,
  Component,
  onlyDestroyOnResolve,
}: {
  width?: number
  Component(props: ManagedDialogProps<Res, Data>): JSX.Element
  // If true, the modal will only be destroyed when the promise is resolved
  // this is needed if you want to remove the underlying component and keep the modal
  // for example if you want to trigger a modal from a tooltip.
  onlyDestroyOnResolve?: boolean
}) {
  const outsideClickCbRef = useRef<(() => void) | null>(null)
  function registerOutsideClick(func: () => void) {
    outsideClickCbRef.current = func
  }

  const Node = useCallback(
    ({ showDialog, data, onResolve }) => (
      <Modal
        width={width}
        onRequestClose={() => {
          outsideClickCbRef.current?.()
        }}
        isOpen={showDialog}
        render={() => (
          <div>
            <Component
              dialogData={data}
              onResolve={onResolve}
              registerOutsideClick={registerOutsideClick}
            />
          </div>
        )}
      />
    ),
    [width],
  )

  const modalRef = useRef<any>(null)

  useEffect(() => {
    modalRef.current = window.MANAGED_MODALS.create()

    return () => {
      if (!onlyDestroyOnResolve) {
        modalRef.current.destroy()
      }
    }
  }, [])

  const isOpenRef = useRef(false)
  const openModal = (data: Data) => {
    if (isOpenRef.current) {
      console.warn("Modal is already open, ignoring open request")
      return Promise.resolve(null)
    }
    isOpenRef.current = true
    const exploded = explodePromise<Res>()

    const onResolve = (response: Res) => exploded.resolve(response)
    // show the modal
    modalRef.current.setComponent(Node, { showDialog: false, data, onResolve })
    setTimeout(() => modalRef.current.setProps({ showDialog: true, data, onResolve }), 10)
    exploded.promise.then(() => {
      modalRef.current.setProps({ showDialog: false })
      setTimeout(() => {
        modalRef.current.setComponent(() => null, {})
        isOpenRef.current = false
      }, 300)
    })

    return exploded.promise
  }

  return openModal
}
